'use client';

import { Sys } from 'libs/contentful/types';
import { cn } from 'utils/styles';
import { usePage } from 'contexts/PageProvider';
import Button from '../Buttons/BaseButton';
import {
  applyEnrollmentParams,
  constructEnrollmentUrlWithQueryParams,
  getCTAType,
  getLinkType,
} from 'utils/linkHelpers';
import { EMAIL_CAPTURE_COOKIE, createCookieOptions } from 'utils/cookies';
import { SubmitHandler, useForm } from 'react-hook-form';
import { setCookie } from 'cookies-next';
import { useAnalyticsModuleTracking } from 'components/analytics/AnalyticsModuleTracking';
import { useLandingPageEmailTracking, useLeadGenerationInteraction } from 'components/analytics/hooks';
import { convertObjectToArray } from 'components/analytics/utils';
import { EMAIL_REGEX } from 'utils/formatters';
import { useEffect, useState, useRef } from 'react';
import Input from '../Input/Input';
import { trackLandingPageEmailFilled, trackEnrollmentCtaTapped } from 'components/analytics/eventHandlerTracking';
import { LEAD_GENERATION_INTERACTION_TYPES } from 'components/analytics/types';
import { addSpeculationRule } from '../../../utils/getSpeculationRules';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { convertCustomQspsToObject } from 'utils/formatters';
import { getCustomQueryStringParametersItemsForClient } from 'utils/pageHelpers/customQueryStringParametersClient';
import { PageContextValue } from 'contexts/pageContextValue';
import { CustomQueryStringParameterItems } from 'libs/contentful/types';

export interface EmailCaptureProps {
  formClassName?: string;
  buttonClassName?: string;
  buttonText: string;
  sys?: Sys;
  appliedPromoCode?: string;
  baseEnrollmentUrl: string;
}

interface FormData {
  email: string;
}

interface CarouselEmailSyncEvent {
  email: string;
  sourceElement: HTMLInputElement;
}

// Custom event name for syncing carousel email inputs
const CAROUSEL_EMAIL_SYNC_EVENT = 'carouselEmailSync';

const constructEnrollmentUrl = (
  page: PageContextValue | undefined,
  baseEnrollmentUrl: string,
  appliedPromoCode?: string,
  customQspsOverride?: CustomQueryStringParameterItems[] | null,
): string => {
  const additionalParams = appliedPromoCode
    ? {
        promo: appliedPromoCode.toLowerCase(),
        ad: `dmail_${appliedPromoCode.toLowerCase()}`,
      }
    : undefined;

  const customItems = Array.isArray(customQspsOverride)
    ? customQspsOverride
    : getCustomQueryStringParametersItemsForClient(page);
  const customParams = convertCustomQspsToObject(customItems);

  const allParams = {
    ...additionalParams,
    ...customParams,
  };

  return constructEnrollmentUrlWithQueryParams(baseEnrollmentUrl, allParams);
};

const EmailCapture = ({
  formClassName,
  buttonClassName,
  sys,
  buttonText,
  appliedPromoCode,
  baseEnrollmentUrl,
}: EmailCaptureProps) => {
  const { page, resolvedCustomQueryStringParametersOverride } = usePage();
  const customQsps = Array.isArray(resolvedCustomQueryStringParametersOverride)
    ? resolvedCustomQueryStringParametersOverride
    : undefined;
  const enrollmentUrl = constructEnrollmentUrl(page, baseEnrollmentUrl, appliedPromoCode, customQsps);
  const [landingInteractionCaptured, setLandingInteractionCaptured] = useState<boolean>(false);

  useEffect(() => {
    return addSpeculationRule(applyEnrollmentParams(enrollmentUrl));
  }, [enrollmentUrl]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useForm<FormData>({ mode: 'onBlur', defaultValues: { email: '' } });

  // Destructure the register result to preserve react-hook-form's onBlur
  const {
    onBlur: registerOnBlur,
    ref: registerRef,
    ...registerRest
  } = register('email', {
    pattern: EMAIL_REGEX,
    required: false,
  });

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { attributes } = useAnalyticsModuleTracking();
  const interactionType = LEAD_GENERATION_INTERACTION_TYPES.ENROLLMENT_CTA_TAPPED;

  const analyticsProps = {
    interaction_type: interactionType,
    button_location: attributes.moduleType,
    interaction_details: {
      location: enrollmentUrl,
      action_detail: buttonText,
    },
    email_entered: false,
    ignored_qsps: {},
    user_alias: {
      alias_label: 'pre_enrollment',
      alias_name: {},
    },
    additional_data: convertObjectToArray({
      destination: enrollmentUrl,
      label: buttonText,
      button_name: buttonText,
      cta_type: getCTAType(interactionType, true),
      link_type: getLinkType(enrollmentUrl),
    }),
  };

  const trackLeadGenEnrollmentCTATapped = useLeadGenerationInteraction(analyticsProps);
  const trackLandingPageEmailInteraction = useLandingPageEmailTracking();

  // Listen for carousel email sync events from other inputs
  useEffect(() => {
    const handleCarouselSync = (event: Event) => {
      const customEvent = event as CustomEvent<CarouselEmailSyncEvent>;
      const { email, sourceElement } = customEvent.detail;

      // Don't update if this is the source element
      if (inputRef.current === sourceElement) return;

      // Update this form's value and trigger validation
      setValue('email', email, { shouldValidate: false });
      trigger('email');
    };

    const currentInput = inputRef.current;
    currentInput?.addEventListener(CAROUSEL_EMAIL_SYNC_EVENT, handleCarouselSync);

    return () => {
      currentInput?.removeEventListener(CAROUSEL_EMAIL_SYNC_EVENT, handleCarouselSync);
    };
  }, [setValue, trigger]);

  const watchedEmail = watch('email');

  // Track landing page email interaction
  useEffect(() => {
    if (landingInteractionCaptured || !EMAIL_REGEX.test(watchedEmail)) return;

    trackLandingPageEmailInteraction();
    trackLandingPageEmailFilled({
      email: watchedEmail,
      button_location: attributes.moduleType,
    });
    setLandingInteractionCaptured(true);
  }, [watchedEmail, landingInteractionCaptured, trackLandingPageEmailInteraction, attributes.moduleType]);

  const onSubmit: SubmitHandler<FormData> = data => {
    if (!enrollmentUrl) return;
    const email = data.email;

    if (email) {
      setCookie(EMAIL_CAPTURE_COOKIE, email, createCookieOptions({ enableCrossSubdomain: true }));
    }

    const finalUrl = applyEnrollmentParams(enrollmentUrl);

    try {
      trackEnrollmentCtaTapped({
        label: buttonText,
        item_value: enrollmentUrl,
        module: {
          module_title: attributes.moduleTitle,
          module_name: attributes.moduleName,
          module_index: attributes.moduleIndex,
          module_type: attributes.moduleType,
        },
        cta_type: getCTAType(interactionType, true),
        button_location: attributes.moduleType,
      });

      trackLeadGenEnrollmentCTATapped({ email: email, email_entered: !!email });
    } catch (err) {
      // ninetailed.track can throw synchronously in Firefox under strict
      // referrer policy. Don't block the redirect on a tracking failure.
      console.error('Enrollment tracking failed', err);
    }

    window.location.href = finalUrl;
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Call react-hook-form's onBlur first to trigger validation on this input
    registerOnBlur(e);

    // If email input is in a carousel, sync with other email inputs
    const carousel = e.target.closest('.carousel');
    if (carousel) {
      const email = e.target.value;
      const inputs = carousel.querySelectorAll('.email-capture-form input');

      // Dispatch custom event to each input (they'll filter out themselves)
      inputs.forEach(input => {
        const customEvent = new CustomEvent(CAROUSEL_EMAIL_SYNC_EVENT, {
          detail: { email, sourceElement: e.target },
          bubbles: false,
        });
        input.dispatchEvent(customEvent);
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="email-capture-form">
      <div className={cn('flex-col flex items-end gap-[10px] mb-0 h-full', 'md:flex-row w-full', formClassName)}>
        <Input
          label="Enter your email"
          {...registerRest}
          ref={(e: HTMLInputElement | null) => {
            registerRef(e);
            inputRef.current = e;
          }}
          error={'Please enter a valid email address.'}
          invalid={!!errors.email}
          onBlur={handleBlur}
        />
        <Button sys={sys} className={buttonClassName} {...liveUpdateAttrs(sys?.id, 'emailInputButtonText')}>
          {buttonText}
        </Button>
      </div>
    </form>
  );
};

export default EmailCapture;
